import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { ApiError } from "../../api/client";
import {
  thinkingApi,
  tokenFromHash,
  type StepFields,
  type SubmitResult,
  type ThinkingView,
} from "../../api/thinking";
import { usePublicConfig } from "../../api/usePublicConfig";
import { initTelemetry, noopTelemetry, type Telemetry } from "../../telemetry/posthog";
import { CloseStepView, PickStepView, TagStepView } from "./ChoiceSteps";
import { IntroStepView } from "./IntroStepView";
import { TextQ3 } from "./q3/TextQ3";
import type { Q3DeliveryProps } from "./q3/types";
import { TextStepView } from "./TextStepView";
import { ThinkingProgress } from "./ThinkingProgress";

const Q3_DELIVERIES: Record<string, (props: Q3DeliveryProps) => React.ReactNode> = {
  text: TextQ3, // "voice" arrives in phase 2
};
const Q3_STEPS = new Set(["q3_argue", "q3_strong", "q3_weak"]);

const telemetryCache = new Map<string, Telemetry>();

/** PostHog is initialised once per page load (and survives React StrictMode double renders). */
function telemetryFor(distinctId: string, apiKey: string, proxyPath: string): Telemetry {
  let telemetry = telemetryCache.get(distinctId);
  if (!telemetry) {
    telemetry = initTelemetry(apiKey, proxyPath, distinctId);
    telemetryCache.set(distinctId, telemetry);
  }
  return telemetry;
}

function useTelemetry(distinctId: string | undefined): Telemetry {
  const config = usePublicConfig();
  return useMemo(() => {
    if (!distinctId || !config.data) return noopTelemetry;
    return telemetryFor(distinctId, config.data.posthog_api_key, config.data.posthog_proxy_path);
  }, [distinctId, config.data]);
}

export function ThinkingPage() {
  const token = tokenFromHash(window.location.hash);
  const queryClient = useQueryClient();
  const key = ["thinking", token];
  const view = useQuery({
    queryKey: key,
    queryFn: () => thinkingApi.view(token),
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const config = usePublicConfig();
  const telemetry = useTelemetry(view.data?.telemetry_id);

  const setView = (next: ThinkingView) => queryClient.setQueryData(key, next);
  const resync = (error: Error) => {
    if (error instanceof ApiError && error.status === 409) void view.refetch();
  };
  const start = useMutation({
    mutationFn: (extraTime: boolean) => thinkingApi.start(token, extraTime),
    onSuccess: setView,
  });
  const submit = useMutation<SubmitResult, Error, StepFields>({
    mutationFn: (fields) =>
      thinkingApi.submit(token, {
        step: view.data?.step ?? "",
        auto_submitted: false,
        ...fields,
        client_telemetry: telemetry.healthy() ? "ok" : "unavailable",
      }),
    onSuccess: (result) => setView(result.view),
    onError: resync,
  });

  if (!token || view.isError) {
    return (
      <main className="page">
        <h1>This link doesn't work</h1>
        <p role="alert">
          {view.error instanceof ApiError
            ? view.error.message
            : "The link is incomplete. Ask your AI agent for a new one with get_answer_link."}
        </p>
      </main>
    );
  }
  if (view.isPending) return <main className="page">Loading…</main>;

  const current = view.data;
  const busy = start.isPending || submit.isPending;
  const submitText = (text: string, auto: boolean) => submit.mutate({ text, auto_submitted: auto });

  let body: React.ReactNode;
  if (current.kind === "intro") {
    body = <IntroStepView view={current} busy={busy} onStart={(extra) => start.mutate(extra)} />;
  } else if (current.kind === "text" && current.text && Q3_STEPS.has(current.step)) {
    const Delivery = Q3_DELIVERIES[config.data?.q3_delivery ?? "text"] ?? TextQ3;
    const kind = current.step === "q3_strong" ? "strong" : "weak";
    body = (
      <Delivery
        key={current.step}
        step={current.text}
        question={current.question}
        telemetry={telemetry}
        busy={busy}
        rebuttal={
          current.step === "q3_argue" ? undefined : { kind, text: current.text.context ?? "" }
        }
        onArgument={(text, meta) => submitText(text, meta.auto_submitted)}
        onReply={(_kind, text, meta) => submitText(text, meta.auto_submitted)}
      />
    );
  } else if (current.kind === "text" && current.text) {
    body = (
      <TextStepView
        key={current.step}
        step={current.text}
        question={current.question}
        telemetry={telemetry}
        busy={busy}
        onSubmit={submitText}
      />
    );
  } else if (current.kind === "tag") {
    body = <TagStepView key={current.step} view={current} busy={busy} onSubmit={submit.mutate} />;
  } else if (current.kind === "pick") {
    body = <PickStepView key={current.step} view={current} busy={busy} onSubmit={submit.mutate} />;
  } else if (current.kind === "close") {
    body = <CloseStepView key={current.step} view={current} busy={busy} onSubmit={submit.mutate} />;
  } else {
    body = (
      <section>
        <h1>Thank you — you're done</h1>
        <p>Your answers are with the Tekdi team. A person reviews every application.</p>
      </section>
    );
  }

  // With the in-journey LLM slot on, these steps wait for a tailored next prompt.
  const slotStep: Record<string, string> = { q2b: "q2_tag", q3_rebuttals: "q3_argue", q1: "" };
  const waitsForModel = slotStep[config.data?.llm_slot_purpose ?? ""] === current.step;

  return (
    <main className="page thinking">
      {submit.isPending && waitsForModel && (
        <p role="status" className="notice">
          Preparing the next part… The timer starts only when it appears.
        </p>
      )}
      {current.kind !== "done" && (
        <ThinkingProgress step={current.step} progress={current.progress} total={current.total} />
      )}
      {body}
      {submit.isError && !(submit.error instanceof ApiError && submit.error.status === 409) && (
        <p role="alert">{submit.error.message}. Your answer is still here; try again.</p>
      )}
    </main>
  );
}
